/**
 * Scroll Event Bus (INTERNAL)
 * 
 * WHY: Decouples scroll publishers (tabs) from scroll subscribers (containers).
 * 
 * PROBLEM: Tabs need to trigger scrolling, but shouldn't know about:
 * - Whether they're in a modal or on the main page
 * - What container is scrollable
 * - How to calculate scroll offsets
 * 
 * SOLUTION: Event-driven architecture with source-based routing.
 * - Publishers emit events with a source ('window' | 'modal')
 * - Subscribers register for a specific source
 * - Only the matching subscriber handles the event
 * 
 * This allows the same tab component to work in both contexts without modification.
 * 
 * ⚠️ INTERNAL IMPLEMENTATION DETAIL
 * External consumers should use the public API in ../api/use-scroll-detection.ts
 */

export type ScrollSource = 'window' | 'modal';

export interface ScrollToElementEvent {
  element: HTMLElement;
  source: ScrollSource;
  offset?: number;
  onComplete?: () => void;
}

type ScrollHandler = (event: ScrollToElementEvent) => boolean;

const LOG_PREFIX = '[ScrollEventBus]';

class ScrollEventBus {
  private subscribers = new Map<ScrollSource, ScrollHandler>();

  subscribe(source: ScrollSource, handler: ScrollHandler): () => void {
    this.subscribers.set(source, handler);
    console.debug(`${LOG_PREFIX} ${source} subscribed`);
    return () => {
      this.subscribers.delete(source);
      console.debug(`${LOG_PREFIX} ${source} unsubscribed`);
    };
  }

  publish(element: HTMLElement, source: ScrollSource, offset?: number, onComplete?: () => void): void {
    console.debug(`${LOG_PREFIX} Publish from ${source}, element:`, element.id, 'offset:', offset);
    
    const handler = this.subscribers.get(source);
    if (!handler) {
      console.warn(`${LOG_PREFIX} No subscriber for source: ${source}`);
      return;
    }
    
    handler({ element, source, offset, onComplete });
  }

  /**
   * Detect active scroll source based on registered subscribers.
   * Prioritizes 'modal' over 'window' when both are present.
   * Defaults to 'window' if no subscribers are registered.
   */
  getActiveSource(): ScrollSource {
    // Prioritize modal context over window when both exist
    const sources: ScrollSource[] = ['modal', 'window'];
    for (const source of sources) {
      if (this.subscribers.has(source)) {
        return source;
      }
    }
    // Fallback to window if no subscribers registered
    return 'window';
  }

  clear(): void {
    this.subscribers.clear();
  }
}

export const scrollEvents = new ScrollEventBus();
